// Google's Rich Results Test flags date-only JSON-LD dates ("2026-05-06") with "Invalid
// datetime value" and "missing a timezone". Every page's structured data passes through
// plugins/single-page-schema.ts, so this one step gives all of them full ISO 8601 dates:
// a date-only value becomes 09:00 New York time on that day, with that day's offset
// (-04:00 in summer, -05:00 in winter). Values that already carry a time are kept, except
// a time without an offset, which gets the New York offset for its day.
const DATE_KEYS = new Set(["datePublished", "dateModified", "dateCreated", "uploadDate"]);
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const NO_OFFSET = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const TIME_ZONE = "America/New_York";

const offsetFormat = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, timeZoneName: "longOffset" });

// The New York UTC offset at noon UTC on that day, as "-04:00" / "-05:00".
export const newYorkOffset = (day: string) => {
  const name = offsetFormat.formatToParts(new Date(`${day}T12:00:00Z`)).find((part) => part.type === "timeZoneName")?.value ?? "";
  const match = name.match(/GMT([+-]\d{2}:\d{2})/);
  return match ? match[1] : "+00:00";
};

export const toSchemaDateTime = (value: unknown) => {
  if (typeof value !== "string") return value;
  if (DATE_ONLY.test(value)) return `${value}T09:00:00${newYorkOffset(value)}`;
  const noOffset = value.match(NO_OFFSET);
  if (noOffset) return `${value}${newYorkOffset(noOffset[1])}`;
  return value;
};

// Rewrites dates in place; returns true when anything changed.
export const normalizeSchemaDates = (value: any): boolean => {
  if (!value || typeof value !== "object") return false;
  let changed = false;
  for (const [key, child] of Object.entries(value)) {
    if (DATE_KEYS.has(key) && typeof child === "string") {
      const next = toSchemaDateTime(child);
      if (next !== child) {
        value[key] = next;
        changed = true;
      }
    } else if (normalizeSchemaDates(child)) {
      changed = true;
    }
  }
  return changed;
};

type HeadTag = { tag: string; props?: Record<string, any>; innerHTML?: string; textContent?: string };

export const normalizeHeadSchemaDates = (tags: HeadTag[]) => {
  for (const tag of tags) {
    if (tag.tag !== "script" || tag.props?.type !== "application/ld+json") continue;
    const field = typeof tag.innerHTML === "string" ? "innerHTML" : typeof tag.textContent === "string" ? "textContent" : null;
    if (!field) continue;
    let schema: unknown;
    try {
      schema = JSON.parse(tag[field] as string);
    } catch {
      continue;
    }
    if (normalizeSchemaDates(schema)) tag[field] = JSON.stringify(schema);
  }
};
